import { Drawer } from '@arkestro/arkestro-design-system';
import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { useContext } from 'react';
import { useRouter } from 'next/router';
import { UserContext } from '@components/providers/UserProvider/UserProvider';
import { buildDefaultRequestParams } from '@utils/BidRequestDefault';
import { BidRequestData } from 'components/RfxDrawers/api';
import { alertMessage } from '@utils/common';
import { useSpendCategories } from '@components/RfxDrawers/hooks/useSpendCategories';
import { useCreateNewRfx } from '@components/RfxDrawers/hooks/useCreateNewRfx';
import { Footer, OverviewPanel } from '@components/RfxDrawers/shared';
import { useGetCompanyTags } from '@hooks/useGetCompanyTags';
import sharedStyles from '@components/RfxDrawers/shared/SharedStyles.module.scss';

interface RequestForInformationFormProps {
  businessUnitId: number | null;
  contractLength: number | null;
  contractLengthUnit: string;
  eventDescription: string;
  eventTitle: string;
  isTemplate: boolean;
  isTest: boolean;
  quoteType: string | null;
  spendTypeId: number | null;
  tagNames: string[];
}

interface RfiDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RfiDrawer = ({ isOpen, onClose }: RfiDrawerProps) => {
  const { t } = useTranslation('common');

  const user = useContext(UserContext);
  const router = useRouter();
  const { data: spendCategories, isLoading: isSpendCategoriesLoading } = useSpendCategories();
  const { data: companyTags = [] } = useGetCompanyTags({ companyId: user?.company?.id, enabled: isOpen });
  const { mutate, isPending: isSavingNewRfi } = useCreateNewRfx();
  const disableFields = isSpendCategoriesLoading || isSavingNewRfi;

  const methods = useForm<RequestForInformationFormProps>({
    defaultValues: {
      businessUnitId: null,
      contractLength: null,
      contractLengthUnit: 'years',
      eventDescription: '',
      eventTitle: '',
      isTemplate: false,
      isTest: false,
      quoteType: null,
      spendTypeId: null,
      tagNames: user?.tag_names || [],
    }
  });

  const { handleSubmit, formState: { isValid }, reset } = methods;

  const onSubmit = (data: RequestForInformationFormProps) => {
    const defaultRFIParams = buildDefaultRequestParams(user, 'information', {}, t);

    const rfiParams = {
      ...defaultRFIParams,
      business_unit_id: data.businessUnitId,
      contract_length: data.contractLength ?? 3, // default to 3 if no contract length is provided
      contract_length_unit: data.contractLengthUnit,
      description: data.eventDescription,
      is_test: data.isTest,
      name: data.eventTitle,

      // Default value is false, so the only time it's true is when quoteType is 'one_time'
      // quoteType for 'price_agreement' won't change anything
      one_off: data.quoteType === 'one_time',
      // camelCase is intentional
      spendCategoryId: data.spendTypeId,
      tag_names: data.tagNames,
      template: data.isTemplate,
    };

    mutate(rfiParams as BidRequestData, {
      onError: () => {
        alertMessage(t('rfx_drawer.creation_error'), 'error', 10);
      },
      onSuccess: responseData => {
        onClose();
        reset();

        const requestId = responseData.bid_request.id;

        router.push(`/bid_requests/${requestId}/dashboard`);
      }
    });
  };

  return (
    <Drawer
      classNames={{ body: isSpendCategoriesLoading ? undefined : sharedStyles.drawerBody, footer: sharedStyles.drawerFooter, header: sharedStyles.drawerHeader }}
      footer={<Footer isSaving={isSavingNewRfi} isValid={isValid} onClose={onClose} onSubmit={handleSubmit(onSubmit)} type='RFI' />}
      loading={isSpendCategoriesLoading}
      onClose={onClose}
      open={isOpen}
      title={t('rfx_drawer.rfi.title')}
      width={680}
    >
      <FormProvider {...methods}>
        <form>
          <OverviewPanel
            canManageTags // always true for creation flows — creator becomes the event owner
            companyTags={companyTags}
            disableFields={disableFields}
            showTags
            spendCategories={spendCategories || []}
          />
        </form>
      </FormProvider>
    </Drawer>
  );
};
